import * as React from 'react';
import propz from 'propz';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { Link } from 'react-router-dom';
import { EmptyComponent } from '../../components/EmptyComponent/EmptyComponent';
import { PersonalDetailsForm } from './PersonalDetailsForm/PersonalDetailsForm';
import { register } from '../../helpers/service/nobody/register';
import { login } from '../../helpers/auth/auth';
import { Loader } from '../../components/Loader/Loader';
import { VerificationForm } from './VerificationForm/VerificationForm';
import { SimpleModal } from '../../components/SimpleModal/SimpleModal';
import { EmailChangeForm } from '../GenericView/Profile/VerificationTab/EmailChangeForm/EmailChangeForm';
import { PhoneChangeForm } from '../GenericView/Profile/VerificationTab/PhoneChangeForm/PhoneChangeForm';
import {
  changeEmail,
  changePhone,
  createProfileRequest,
  getProfile,
  parentAutoLink,
  resendEmailCode,
  resendPhoneCode
} from '../../helpers/service/admin/user';
import { verifyEmail, verifyPhone } from '../../helpers/service/nobody/confirm';
import { Permission } from './Permission/Permission';
import StorageHelper from '../../helpers/storage/storage';
import { replaceLoginSession } from '../../helpers/session/session';
import { LoginSession } from '../../models/sessions';
import { AppUser } from '../App/App';
import { UserProfile } from '../../models/profile';
import { ROLE } from '../../consts/user';
import { SCHOOL_PERMISSION_REQUEST_FIELD_STATUS } from '../../consts/school';
import { getUKPhoneWithoutFirstZero } from '../../helpers/phone/phone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheckCircle,
  faCircleExclamation,
  faClock,
  faGraduationCap,
  faSchool,
  faShieldAlt,
  faUserFriends
} from '@fortawesome/free-solid-svg-icons';
import './Registration.scss';

export type RegistrationPermission = 'STAFF' | 'STUDENT' | 'PARENT';

export const REGISTRATION_PERMISSION: {
  [key: string]: RegistrationPermission;
} = {
  STAFF: 'STAFF',
  STUDENT: 'STUDENT',
  PARENT: 'PARENT'
};

export type RegistrationStaff = 'ADMIN' | 'MANAGER' | 'COACH' | 'TEACHER';

export const REGISTRATION_STAFF_PRESET: {
  [key: string]: RegistrationStaff;
} = {
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  COACH: 'COACH',
  TEACHER: 'TEACHER'
};

type Step = 'DETAILS' | 'VERIFICATION' | 'PERMISSION';

type VisualRegistrationStep = {
  id: 'DETAILS' | 'VERIFICATION' | 'ROLE' | 'SCHOOL';
  label: string;
};

const VISUAL_REGISTRATION_STEPS: VisualRegistrationStep[] = [
  { id: 'DETAILS', label: 'Your details' },
  { id: 'VERIFICATION', label: 'Verification' },
  { id: 'ROLE', label: 'Your role' },
  { id: 'SCHOOL', label: 'Your school' }
];

const REGISTRATION_WIZARD_STEP: {
  [key: string]: Step;
} = {
  DETAILS: 'DETAILS',
  VERIFICATION: 'VERIFICATION',
  PERMISSION: 'PERMISSION'
};

interface Props {
  history: History;
  location: Location;
  user: AppUser;
  onFormSubmit: (userId: string, key: string, profile: UserProfile, options?: { isRolesNotExist?: boolean }) => void;
}
interface State {
  currentStep: Step;
  isLoading: boolean;
  loginSession: LoginSession;
  email: string;
  phone: string;

  isResendPhoneCodeModalOpen: boolean;
  isResendEmailCodeModalOpen: boolean;
  isChangePhoneModalOpen: boolean;
  isChangeEmailModalOpen: boolean;
  isSuccessPhoneChangeModalOpen: boolean;
  isSuccessEmailChangeModalOpen: boolean;

  isEmailConfirmed: boolean;
  isPhoneConfirmed: boolean;

  permission: RegistrationPermission;
  lastPermission?: RegistrationPermission;
  staff: RegistrationStaff;

  isRoleStudentRequestModalOpen: boolean;
  isRoleFirstStaffRequestModalOpen: boolean;
  isRoleStaffRequestModalOpen: boolean;
  isRoleParentRequestModalOpen: boolean;

  school: any;
  schoolId: string;
  oneOffCode: string;

  isStaffRoleSelectorEnabled: boolean;
  isParentDetailsEnabled: boolean;

  students: any[];
  parentDetailsDraft?: any[];

  // Auto-link result state
  autoLinkedStudents: Array<{ studentId: string; studentName: string }>;
  isAutoLinkSuccessModalOpen: boolean;

  phoneActionError?: string;
  emailActionError?: string;

  registrationActionError?: string;

  registrationRetryUntilTs?: number;
  registrationRetryAfterSec?: number;

  personalDetailsDraft?: any;

  registrationCooldownUntilMs?: number;
  registrationCooldownCode?: string;

  emailCooldownUntilMs?: number;
  emailCooldownCode?: string;

  phoneCooldownUntilMs?: number;
  phoneCooldownCode?: string;
  cooldownTick?: number;

  emailUpdateCooldownUntilMs?: number;
  emailUpdateCooldownCode?: string;

  phoneUpdateCooldownUntilMs?: number;
  phoneUpdateCooldownCode?: string;

  emailVerifyCooldownUntilMs?: number;
  emailVerifyCooldownCode?: string;

  phoneVerifyCooldownUntilMs?: number;
  phoneVerifyCooldownCode?: string;

  emailResendError?: string;
  emailVerifyError?: string;
  phoneResendError?: string;
  phoneVerifyError?: string;

  registrationDone?: 'adminTrial';
  registrationDoneProfile?: UserProfile;
  trialModules: string[];
}

function GateAlert(props: { text: string; code?: string }) {
  const { text, code } = props;
  const isBlocked = code === 'AUTO_BLOCKED' || code === 'ENDPOINT_DISABLED';

  const title =
    code === 'ENDPOINT_DISABLED'
      ? 'Temporarily unavailable'
      : isBlocked
      ? 'Temporarily unavailable'
      : 'Please wait a moment';

  return (
    <div className={`eCooldownAlert ${isBlocked ? 'mBlocked' : 'mRateLimited'}`}>
      <div className="eCooldownAlertTop">
        <div className="eCooldownAlertLeft">
          <div className="eCooldownAlertIcon">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </div>

          <div className="eCooldownAlertText">
            <div className="eCooldownAlertTitle">{title}</div>
            <div className="eCooldownAlertMessage">{normalizeCooldownText(text)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeCooldownText(text: string) {
  if (!text) return text;

  text = text.replace(/(\.|!|\s)?\s*Please try again in\s+\d+\s+seconds\.?/gi, '');

  text = text.replace(/(\.|!|\s)?\s*Try again in\s+\d+\s+seconds\.?/gi, '');

  text = text.replace(/\s*in\s+\d+\s+seconds\.?/gi, '');

  return text.trim();
}

function RegistrationCooldownAlert(props: {
  text: string;
  code?: string;
  getRemainingSec: () => number;
  formatMmSs: (s: number) => string;
  onCooldownFinished: () => void;
}) {
  const { text, code, getRemainingSec, formatMmSs, onCooldownFinished } = props;
  const cleanText = normalizeCooldownText(text);

  const [sec, setSec] = React.useState<number>(getRemainingSec());

  React.useEffect(() => {
    const id = window.setInterval(() => {
      const s = getRemainingSec();
      setSec(s);
      if (s <= 0) {
        window.clearInterval(id);
        onCooldownFinished();
      }
    }, 250);

    return () => window.clearInterval(id);
  }, []);

  const isBlocked = code === 'AUTO_BLOCKED';

  const title = isBlocked ? 'Temporarily unavailable' : 'Please wait a moment';
  const subtitle = sec > 0 ? `Try again in ${sec} seconds.` : undefined;

  return (
    <div className={`eCooldownAlert ${isBlocked ? 'mBlocked' : 'mRateLimited'}`}>
      <div className="eCooldownAlertTop">
        <div className="eCooldownAlertLeft">
          <div className="eCooldownAlertIcon">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </div>

          <div className="eCooldownAlertText">
            <div className="eCooldownAlertTitle">{title}</div>
            <div className="eCooldownAlertMessage">{cleanText}</div>
            {subtitle && <div className="eCooldownAlertHint">{subtitle}</div>}
          </div>
        </div>

        {sec > 0 && <div className="eCooldownAlertTimer">{formatMmSs(sec)}</div>}
      </div>
    </div>
  );
}

const ModalTitle = (props: { icon: any; title: string; tone?: 'success' | 'warning' | 'info' }) => {
  const { icon, title, tone = 'info' } = props;
  return (
    <div className={`eModalTitleRow m${tone}`}>
      <span className="eModalTitleIcon">
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className="eModalTitleText">{title}</span>
    </div>
  );
};

const ModalBody = (props: { lines: React.ReactNode[]; hint?: React.ReactNode }) => {
  const { lines, hint } = props;
  return (
    <div className="eModalBody">
      {lines.map((l, i) => (
        <div key={i} className="eModalBodyLine">
          {l}
        </div>
      ))}
      {hint && <div className="eModalBodyHint">{hint}</div>}
    </div>
  );
};

export class Registration extends React.Component<Props, State> {
  private registrationCooldownTimer?: any;

  componentDidUpdate(prevProps: Props, prevState: State) {
    const hadCooldown =
      !!prevState.registrationCooldownUntilMs ||
      !!prevState.emailCooldownUntilMs ||
      !!prevState.phoneCooldownUntilMs ||
      !!prevState.emailVerifyCooldownUntilMs ||
      !!prevState.phoneVerifyCooldownUntilMs;

    const hasCooldown =
      !!this.state.registrationCooldownUntilMs ||
      !!this.state.emailCooldownUntilMs ||
      !!this.state.phoneCooldownUntilMs ||
      !!this.state.emailVerifyCooldownUntilMs ||
      !!this.state.phoneVerifyCooldownUntilMs;

    if (!hadCooldown && hasCooldown) {
      this.startCooldownTicker();
    }

    if (hadCooldown && !hasCooldown) {
      this.stopCooldownTicker();
    }
  }

  componentWillUnmount() {
    this.stopCooldownTicker();
  }

  private startCooldownTicker = () => {
    if (this.registrationCooldownTimer) return;

    this.registrationCooldownTimer = window.setInterval(() => {
      this.setState({ cooldownTick: Date.now() });
    }, 250);
  };

  private stopCooldownTicker = () => {
    if (this.registrationCooldownTimer) {
      window.clearInterval(this.registrationCooldownTimer);
      this.registrationCooldownTimer = undefined;
    }
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      currentStep: REGISTRATION_WIZARD_STEP.DETAILS,
      isLoading: false,
      loginSession: undefined,
      email: '',
      phone: '',

      isResendPhoneCodeModalOpen: false,
      isResendEmailCodeModalOpen: false,
      isChangePhoneModalOpen: false,
      isChangeEmailModalOpen: false,
      isSuccessPhoneChangeModalOpen: false,
      isSuccessEmailChangeModalOpen: false,

      isEmailConfirmed: undefined,
      isPhoneConfirmed: undefined,
      permission: undefined,
      lastPermission: undefined,
      staff: undefined,
      isRoleStudentRequestModalOpen: false,

      school: undefined,
      schoolId: '',
      oneOffCode: '',
      isStaffRoleSelectorEnabled: false,
      isParentDetailsEnabled: false,
      isRoleFirstStaffRequestModalOpen: false,
      isRoleStaffRequestModalOpen: false,
      isRoleParentRequestModalOpen: false,

      students: [],
      parentDetailsDraft: undefined,

      autoLinkedStudents: [],
      isAutoLinkSuccessModalOpen: false,

      phoneActionError: undefined,
      emailActionError: undefined,

      registrationActionError: undefined,

      personalDetailsDraft: undefined,

      registrationCooldownUntilMs: undefined,
      registrationCooldownCode: undefined,

      emailCooldownUntilMs: undefined,
      emailCooldownCode: undefined,

      phoneCooldownUntilMs: undefined,
      phoneCooldownCode: undefined,

      emailUpdateCooldownUntilMs: undefined,
      emailUpdateCooldownCode: undefined,
      phoneUpdateCooldownUntilMs: undefined,
      phoneUpdateCooldownCode: undefined,

      emailVerifyCooldownUntilMs: undefined,
      emailVerifyCooldownCode: undefined,

      phoneVerifyCooldownUntilMs: undefined,
      phoneVerifyCooldownCode: undefined,

      emailResendError: undefined,
      emailVerifyError: undefined,
      phoneResendError: undefined,
      phoneVerifyError: undefined,

      registrationDone: undefined,
      registrationDoneProfile: undefined,
      trialModules: []
    };
  }

  componentDidMount() {
    const { user, history } = this.props;
    const isEmailVerified = propz.get(user, ['profile', 'verification', 'status', 'email']);
    const isPhoneVerified = propz.get(user, ['profile', 'verification', 'status', 'sms']);

    // A refresh after the account has been created loses the wizard's local
    // step state. Continue with the new account's canonical verification page
    // instead of offering to register the same email again.
    if (user.authorized && (!isEmailVerified || !isPhoneVerified)) {
      history.replace('/profile/verification');
    }
  }

  getRequestedRole = () => {
    const search = String(this.props.location && this.props.location.search ? this.props.location.search : '');
    const role = new URLSearchParams(search).get('role');
    return String(role || '')
      .trim()
      .toLowerCase();
  };

  getEmailHint = () => {
    switch (this.getRequestedRole()) {
      case 'staff':
        return 'This is also your login ID. A school domain speeds up approval.';
      case 'parent':
        return 'This is also your login ID. Using the email address your school holds for you speeds up approval.';
      default:
        return 'This email address is also your login ID.';
    }
  };

  getVisualStepNumber = () => {
    const { currentStep, permission, registrationDone } = this.state;

    if (registrationDone) return 5;
    if (currentStep === REGISTRATION_WIZARD_STEP.DETAILS) return 1;
    if (currentStep === REGISTRATION_WIZARD_STEP.VERIFICATION) return 2;
    return permission ? 4 : 3;
  };

  onCancelClick = () => {
    const { history } = this.props;

    history.push({
      pathname: '/login'
    });
  };

  onSubmitPersonalDetailsClick = data => {
    this.setState({
      isLoading: true,
      registrationActionError: undefined,
      personalDetailsDraft: {
        ...data,
        confirmPassword: ''
      }
    });

    const { phone, phoneCode, confirmPassword, terms, additionalTerms, ...rest } = data;
    const hasPhone = typeof phone === 'string' && phone.trim().length > 0;
    const phoneWithoutFirstZero = hasPhone ? getUKPhoneWithoutFirstZero(phone, phoneCode) : '';
    const normalizedPhone = hasPhone ? `${phoneCode}${phoneWithoutFirstZero}` : '';

    const dataToPost = hasPhone
      ? {
          ...rest,
          phone: normalizedPhone
        }
      : rest;

    register(dataToPost)
      .then(registeredUser => login(data.email, data.password).then(session => ({ session, registeredUser })))
      .then(({ session, registeredUser }) => {
        const smsStatus = propz.get(registeredUser, ['verification', 'status', 'sms']);

        // Registration creates a real login session. Promote it immediately so
        // navigation or another tab can never fall back to a previously logged
        // in user's role/school context while verification is still in progress.
        replaceLoginSession(session);
        this.props.onFormSubmit(session.userId, session.key, registeredUser);

        this.setState({
          isLoading: false,
          currentStep: REGISTRATION_WIZARD_STEP.VERIFICATION,
          loginSession: session,
          email: data.email,
          phone: normalizedPhone,
          isPhoneConfirmed: typeof smsStatus === 'boolean' ? smsStatus : !hasPhone,
          phoneActionError: undefined,
          emailActionError: undefined,
          registrationActionError: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;
        const httpStatus =
          Number(propz.get(payload, ['httpStatus'], propz.get(e, ['response', 'status']))) || undefined;

        const text = this.extractErrText(e, 'Unable to register right now. Please try again later.');

        this.setState({
          isLoading: false,
          currentStep: REGISTRATION_WIZARD_STEP.DETAILS,
          registrationActionError: text,
          registrationCooldownUntilMs: retryAfterSec > 0 ? Date.now() + retryAfterSec * 1000 : undefined,
          registrationCooldownCode: code || (httpStatus ? String(httpStatus) : undefined)
        });
      });
  };

  onSubmitVerificationClick = () => {
    this.setState({
      currentStep: REGISTRATION_WIZARD_STEP.PERMISSION
    });
  };

  onStaffClick = (staff: RegistrationStaff) => {
    this.setState({
      staff: staff,
      isRoleStaffRequestModalOpen: true
    });
  };

  onPermissionClick = (permission: RegistrationPermission) => {
    this.setState({
      permission: permission,
      lastPermission: permission
    });
  };

  onBackToPermissionSelector = (schoolDraft?: any) => {
    this.setState({
      permission: undefined,
      school: schoolDraft ? schoolDraft.school : this.state.school,
      schoolId: schoolDraft ? schoolDraft.schoolId : this.state.schoolId,
      oneOffCode: schoolDraft ? schoolDraft.oneOffCode : this.state.oneOffCode,
      isStaffRoleSelectorEnabled: false,
      isParentDetailsEnabled: false,
      isRoleStudentRequestModalOpen: false,
      isRoleFirstStaffRequestModalOpen: false,
      isRoleStaffRequestModalOpen: false,
      isRoleParentRequestModalOpen: false,
      registrationActionError: undefined
    });
  };

  onBackToSchoolPicker = () => {
    this.setState({
      isStaffRoleSelectorEnabled: false,
      isParentDetailsEnabled: false,
      isRoleStudentRequestModalOpen: false,
      isRoleFirstStaffRequestModalOpen: false,
      isRoleStaffRequestModalOpen: false,
      isRoleParentRequestModalOpen: false,
      registrationActionError: undefined
    });
  };

  onBackFromParentDetails = (parentDetailsDraft: any[]) => {
    this.setState({
      parentDetailsDraft: parentDetailsDraft.map(student => ({ ...student })),
      isParentDetailsEnabled: false,
      isRoleParentRequestModalOpen: false,
      registrationActionError: undefined
    });
  };

  onSubmitStaffPermission = data => {
    const { school, schoolId } = data;

    const isStaffExist = propz.get(school, ['stats', 'rolesExistence', 'staff'], true);

    if (!isStaffExist) {
      this.setState({
        school,
        schoolId,
        registrationActionError: undefined,
        isRoleFirstStaffRequestModalOpen: true
      });
    } else {
      this.setState({
        school,
        schoolId,
        isStaffRoleSelectorEnabled: true
      });
    }
  };

  onSubmitStudentPermission = data => {
    const { school, schoolId, oneOffCode } = data;

    if (propz.get(school, ['stats', 'rolesExistence', 'staff']) === false) {
      this.setState({ school, schoolId, oneOffCode });
      return;
    }

    this.setState({
      school,
      schoolId,
      oneOffCode,
      isRoleStudentRequestModalOpen: true
    });
  };

  isAllRequestFieldsHidden(school: any): boolean {
    const fields = school.additionalPermissionRequestFields;

    return (
      fields.childFirstName === SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN &&
      fields.childLastName === SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN &&
      fields.childDateOfBirth === SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN &&
      fields.childGender === SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN &&
      fields.childForm === SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN &&
      fields.childHouse === SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN
    );
  }

  onSubmitParentPermission = data => {
    const { school, schoolId } = data;
    const parentDetailsDraft =
      this.state.schoolId && this.state.schoolId !== schoolId ? undefined : this.state.parentDetailsDraft;

    if (propz.get(school, ['stats', 'rolesExistence', 'staff']) === false) {
      this.setState({ school, schoolId, parentDetailsDraft });
      return;
    }

    const isAutoAccept = school && school.isParentsAndStudentsAutoAccept === true;

    // If the school has auto-accept enabled, attempt auto-link.
    // The backend handles allowedParentRelationships filtering — an empty list
    // means no relationships are configured yet and will return linkedCount: 0.
    if (isAutoAccept) {
      this.setState({ isLoading: true, school, schoolId, parentDetailsDraft });

      parentAutoLink({ loginSessionKey: this.state.loginSession.key }, schoolId)
        .then(result => {
          if (result.linkedCount > 0) {
            this.setState({
              isLoading: false,
              autoLinkedStudents: result.linkedStudents,
              isAutoLinkSuccessModalOpen: true
            });
          } else {
            const isAllFieldsHidden = this.isAllRequestFieldsHidden(school);
            if (isAllFieldsHidden) {
              this.setState({ isLoading: false, isRoleParentRequestModalOpen: true });
            } else {
              this.setState({ isLoading: false, isParentDetailsEnabled: true });
            }
          }
        })
        .catch(err => {
          console.error('[AutoLink] error:', err);
          const isAllFieldsHidden = this.isAllRequestFieldsHidden(school);
          if (isAllFieldsHidden) {
            this.setState({ isLoading: false, isRoleParentRequestModalOpen: true });
          } else {
            this.setState({ isLoading: false, isParentDetailsEnabled: true });
          }
        });

      return;
    }

    // Default path — auto-accept not enabled for this school
    const isAllFieldsHidden = this.isAllRequestFieldsHidden(school);

    if (isAllFieldsHidden) {
      this.setState({
        school,
        schoolId,
        parentDetailsDraft,
        isRoleParentRequestModalOpen: true
      });
    } else {
      this.setState({
        school,
        schoolId,
        parentDetailsDraft,
        isParentDetailsEnabled: true
      });
    }
  };

  onSubmitParentDetails = (data, parentDetailsDraft?: any[]) => {
    this.setState({
      isRoleParentRequestModalOpen: true,
      students: [...data],
      parentDetailsDraft: parentDetailsDraft
        ? parentDetailsDraft.map(student => ({ ...student }))
        : this.state.parentDetailsDraft
    });
  };

  getEmailResendCooldownSecRemaining = () => this.getRemainingSec(this.state.emailCooldownUntilMs);
  getPhoneResendCooldownSecRemaining = () => this.getRemainingSec(this.state.phoneCooldownUntilMs);

  getEmailVerifyCooldownSecRemaining = () => this.getRemainingSec(this.state.emailVerifyCooldownUntilMs);
  getPhoneVerifyCooldownSecRemaining = () => this.getRemainingSec(this.state.phoneVerifyCooldownUntilMs);

  renderViewByStep() {
    const {
      currentStep,
      loginSession,
      email,
      phone,
      isPhoneConfirmed,
      isEmailConfirmed,
      permission,
      isStaffRoleSelectorEnabled,
      isParentDetailsEnabled,
      isAutoLinkSuccessModalOpen,
      autoLinkedStudents,
      staff,
      school
    } = this.state;

    switch (currentStep) {
      case REGISTRATION_WIZARD_STEP.DETAILS:
        return (
          <>
            {this.state.registrationActionError &&
              (this.getCooldownSecRemaining() > 0 ? (
                <RegistrationCooldownAlert
                  text={this.state.registrationActionError}
                  code={this.state.registrationCooldownCode}
                  getRemainingSec={this.getCooldownSecRemaining}
                  formatMmSs={this.formatMmSs}
                  onCooldownFinished={() =>
                    this.setState({
                      registrationCooldownUntilMs: undefined,
                      registrationCooldownCode: undefined,
                      registrationActionError: undefined
                    })
                  }
                />
              ) : (
                <GateAlert text={this.state.registrationActionError} code={this.state.registrationCooldownCode} />
              ))}

            <PersonalDetailsForm
              onSubmit={this.onSubmitPersonalDetailsClick}
              onCancel={this.onCancelClick}
              initialValues={this.state.personalDetailsDraft}
              cooldownSec={this.getCooldownSecRemaining()}
              emailHint={this.getEmailHint()}
            />
          </>
        );
      case REGISTRATION_WIZARD_STEP.VERIFICATION:
        return (
          <>
            <VerificationForm
              onSubmit={this.onSubmitVerificationClick}
              onCancel={() => this.setState({ currentStep: REGISTRATION_WIZARD_STEP.DETAILS })}
              email={email}
              phone={phone}
              onSendEmailCodeAgainClick={this.onSendEmailCodeAgainClick}
              onChangeEmailClick={this.onChangeEmailClick}
              onSendPhoneCodeAgainClick={this.onSendPhoneCodeAgainClick}
              onChangePhoneClick={this.onChangePhoneClick}
              isPhoneConfirmed={isPhoneConfirmed}
              isEmailConfirmed={isEmailConfirmed}
              onSubmitEmailClick={this.onSubmitEmailClick}
              onSubmitPhoneClick={this.onSubmitPhoneClick}
              emailResendError={this.state.emailResendError}
              emailVerifyError={this.state.emailVerifyError}
              phoneResendError={this.state.phoneResendError}
              phoneVerifyError={this.state.phoneVerifyError}
              emailCooldownSec={this.getEmailResendCooldownSecRemaining()}
              phoneCooldownSec={this.getPhoneResendCooldownSecRemaining()}
              emailCooldownCode={this.state.emailCooldownCode}
              phoneCooldownCode={this.state.phoneCooldownCode}
              emailVerifyCooldownSec={this.getEmailVerifyCooldownSecRemaining()}
              phoneVerifyCooldownSec={this.getPhoneVerifyCooldownSecRemaining()}
              emailVerifyCooldownCode={this.state.emailVerifyCooldownCode}
              phoneVerifyCooldownCode={this.state.phoneVerifyCooldownCode}
              formatMmSs={this.formatMmSs}
              onEmailResendCooldownFinished={this.onEmailResendCooldownFinished}
              onEmailVerifyCooldownFinished={this.onEmailVerifyCooldownFinished}
              onPhoneResendCooldownFinished={this.onPhoneResendCooldownFinished}
              onPhoneVerifyCooldownFinished={this.onPhoneVerifyCooldownFinished}
            />
          </>
        );
      case REGISTRATION_WIZARD_STEP.PERMISSION:
        return (
          <Permission
            phone={phone}
            onSubmitStudentPermission={this.onSubmitStudentPermission}
            onSubmitStaffPermission={this.onSubmitStaffPermission}
            onSubmitParentPermission={this.onSubmitParentPermission}
            onSubmitParentDetails={this.onSubmitParentDetails}
            onBackToPermissionSelector={this.onBackToPermissionSelector}
            onBackToSchoolPicker={this.onBackToSchoolPicker}
            onBackFromParentDetails={this.onBackFromParentDetails}
            parentDetailsDraft={this.state.parentDetailsDraft}
            initialOneOffCode={this.state.oneOffCode}
            loginSession={loginSession}
            onPermissionClick={this.onPermissionClick}
            permission={permission}
            permissionSelection={permission || this.state.lastPermission}
            isStaffRoleSelectorEnabled={isStaffRoleSelectorEnabled}
            isParentDetailsEnabled={isParentDetailsEnabled}
            isAutoLinkSuccessScreenEnabled={isAutoLinkSuccessModalOpen}
            autoLinkedStudents={autoLinkedStudents}
            staff={staff}
            school={school}
            onStaffClick={this.onStaffClick}
            isFirstStaffTrialConfirmationEnabled={this.state.isRoleFirstStaffRequestModalOpen}
            onConfirmFirstStaffTrial={this.onFirstStaffRoleRequestClick}
            onClearFirstStaffSchool={this.onBackToSchoolPicker}
            firstStaffTrialError={this.state.registrationActionError}
            isStudentRequestConfirmationEnabled={this.state.isRoleStudentRequestModalOpen}
            isStaffRequestConfirmationEnabled={this.state.isRoleStaffRequestModalOpen}
            isParentRequestConfirmationEnabled={this.state.isRoleParentRequestModalOpen}
            onConfirmStudentRequest={this.onStudentRoleRequestClick}
            onConfirmStaffRequest={this.onStaffRoleRequestClick}
            onConfirmParentRequest={this.onParentRoleRequestClick}
            onBackStudentRequest={this.closeStudentRoleRequestModal}
            onBackStaffRequest={this.closeStaffRoleRequestModal}
            onBackParentRequest={this.closeParentRoleRequestModal}
            onContinueAutoLink={this.onAutoLinkSuccessOkClick}
            onClearSelectedSchool={this.onClearSelectedSchool}
          />
        );
      default:
        return <EmptyComponent />;
    }
  }

  closeResendEmailCodeModal = () => {
    this.setState({
      isResendEmailCodeModalOpen: false
    });
  };

  closeResendPhoneCodeModal = () => {
    this.setState({
      isResendPhoneCodeModalOpen: false
    });
  };

  onPhoneChangeFormSubmit = data => {
    if (this.getPhoneUpdateCooldownSecRemaining() > 0) return;

    const { phone, phoneCode } = data;
    const { loginSession } = this.state;

    const phoneWithoutFirstZero = getUKPhoneWithoutFirstZero(phone, phoneCode);
    const phoneToPost = `${phoneCode}${phoneWithoutFirstZero}`;

    this.setState({ isLoading: true, phoneActionError: undefined });

    const user = { loginSessionKey: loginSession.key };

    changePhone(user, phoneToPost, data.captchaToken)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          this.setState({
            isLoading: false,
            phoneActionError: res.message || 'We cannot update your phone right now. Please try again later.',
            phoneUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + retryAfterSec * 1000 : undefined,
            phoneUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
          });

          return;
        }

        this.setState({
          isChangePhoneModalOpen: false,
          isSuccessPhoneChangeModalOpen: true,
          phone: phoneToPost,
          isLoading: false,
          phoneUpdateCooldownUntilMs: undefined,
          phoneUpdateCooldownCode: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        this.setState({
          isLoading: false,
          phoneActionError: this.extractErrText(e, 'We cannot update your phone right now. Please try again later.'),
          phoneUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + retryAfterSec * 1000 : undefined,
          phoneUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onEmailChangeFormSubmit = data => {
    if (this.getEmailUpdateCooldownSecRemaining() > 0) return;

    const { loginSession } = this.state;
    const user = { loginSessionKey: loginSession.key };

    this.setState({
      isLoading: true,
      emailActionError: undefined
    });

    changeEmail(user, data.email, data.captchaToken)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          this.setState({
            isLoading: false,
            emailActionError: res.message || 'We cannot update your email right now. Please try again later.',
            emailUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + retryAfterSec * 1000 : undefined,
            emailUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
          });

          return;
        }

        this.setState({
          isChangeEmailModalOpen: false,
          isSuccessEmailChangeModalOpen: true,
          email: data.email,
          isLoading: false,
          emailUpdateCooldownUntilMs: undefined,
          emailUpdateCooldownCode: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        this.setState({
          isLoading: false,
          emailActionError: this.extractErrText(e, 'We cannot update your email right now. Please try again later.'),
          emailUpdateCooldownUntilMs: retryAfterSec > 0 ? Date.now() + retryAfterSec * 1000 : undefined,
          emailUpdateCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onEmailChangeFormCancelClick = () => {
    this.setState({
      isChangeEmailModalOpen: false
    });
  };

  onPhoneChangeFormCancelClick = () => {
    this.setState({
      isChangePhoneModalOpen: false
    });
  };

  closeSuccessEmailChangeModal = () => {
    this.setState({
      isSuccessEmailChangeModalOpen: false
    });
  };

  closeSuccessPhoneChangeModal = () => {
    this.setState({
      isSuccessPhoneChangeModalOpen: false
    });
  };

  renderResendEmailCodeModal() {
    const { isResendEmailCodeModalOpen, email } = this.state;

    return (
      <SimpleModal
        isOpen={isResendEmailCodeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Verification code sent" tone="success" />}
        body={
          <ModalBody
            lines={[<>We have resent your email verification code.</>, <>Please check your inbox and spam folder.</>]}
            hint={
              <>
                Sent to <strong>{email}</strong>
              </>
            }
          />
        }
        buttonText={'Got it'}
        onButtonClick={this.closeResendEmailCodeModal}
        hideCancelButton={true}
        onCloseClick={this.closeResendEmailCodeModal}
        customClass={'eRegistrationModal eRegistrationSmallModal'}
        isBodyBold={false}
      />
    );
  }

  renderResendPhoneCodeModal() {
    const { isResendPhoneCodeModalOpen, phone } = this.state;

    return (
      <SimpleModal
        isOpen={isResendPhoneCodeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Verification code sent" tone="success" />}
        body={
          <ModalBody
            lines={[
              <>We have resent your phone verification code.</>,
              <>If you don’t receive it, please double-check your phone number.</>
            ]}
            hint={
              <>
                Sent to <strong>{phone}</strong>
              </>
            }
          />
        }
        buttonText={'Got it'}
        onButtonClick={this.closeResendPhoneCodeModal}
        hideCancelButton={true}
        onCloseClick={this.closeResendPhoneCodeModal}
        customClass={'eRegistrationModal eRegistrationSmallModal'}
        isBodyBold={false}
      />
    );
  }

  renderEmailChangeModal() {
    const { isChangeEmailModalOpen, email } = this.state;

    return (
      <SimpleModal
        isOpen={isChangeEmailModalOpen}
        customClass="eChangeContactModal eRegistrationContactModal"
        onCloseClick={this.onEmailChangeFormCancelClick}
        showHeader={false}
        showFooter={false}
      >
        <EmailChangeForm
          onCancel={this.onEmailChangeFormCancelClick}
          onSubmit={this.onEmailChangeFormSubmit}
          email={email}
          cooldownSec={this.getEmailUpdateCooldownSecRemaining()}
          formatMmSs={this.formatMmSs}
          actionError={this.state.emailActionError}
          cooldownCode={this.state.emailUpdateCooldownCode}
          onCooldownFinished={this.onEmailUpdateCooldownFinished}
        />
      </SimpleModal>
    );
  }

  renderPhoneChangeModal() {
    const { isChangePhoneModalOpen, phone } = this.state;

    return (
      <SimpleModal
        isOpen={isChangePhoneModalOpen}
        customClass="eChangeContactModal eRegistrationContactModal"
        onCloseClick={this.onPhoneChangeFormCancelClick}
        showHeader={false}
        showFooter={false}
      >
        <PhoneChangeForm
          onCancel={this.onPhoneChangeFormCancelClick}
          onSubmit={this.onPhoneChangeFormSubmit}
          phone={phone}
          cooldownSec={this.getPhoneUpdateCooldownSecRemaining()}
          formatMmSs={this.formatMmSs}
          actionError={this.state.phoneActionError}
          cooldownCode={this.state.phoneUpdateCooldownCode}
          onCooldownFinished={this.onPhoneUpdateCooldownFinished}
        />
      </SimpleModal>
    );
  }

  renderSuccessEmailChangeModal() {
    const { isSuccessEmailChangeModalOpen, email } = this.state;

    return (
      <SimpleModal
        isOpen={isSuccessEmailChangeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Email updated" tone="success" />}
        body={
          <ModalBody
            lines={[<>We’ve sent a new verification code to your updated email address.</>]}
            hint={
              <>
                Sent to <strong>{email}</strong>
              </>
            }
          />
        }
        buttonText={'Ok'}
        onButtonClick={this.closeSuccessEmailChangeModal}
        hideCancelButton={true}
        onCloseClick={this.closeSuccessEmailChangeModal}
        customClass={'eRegistrationModal eRegistrationSmallModal'}
        isBodyBold={false}
      />
    );
  }

  renderSuccessPhoneChangeModal() {
    const { isSuccessPhoneChangeModalOpen, phone } = this.state;

    return (
      <SimpleModal
        isOpen={isSuccessPhoneChangeModalOpen}
        title={<ModalTitle icon={faCircleExclamation} title="Phone updated" tone="success" />}
        body={
          <ModalBody
            lines={[<>We’ve sent a new verification code to your updated phone number.</>]}
            hint={
              <>
                Sent to <strong>{phone}</strong>
              </>
            }
          />
        }
        buttonText={'Ok'}
        onButtonClick={this.closeSuccessPhoneChangeModal}
        hideCancelButton={true}
        onCloseClick={this.closeSuccessPhoneChangeModal}
        customClass={'eRegistrationModal eRegistrationSmallModal'}
        isBodyBold={false}
      />
    );
  }

  onAutoLinkSuccessOkClick = () => {
    const { loginSession } = this.state;
    const { history, user, onFormSubmit } = this.props;
    const { userId, key } = loginSession;

    this.setState({ isAutoLinkSuccessModalOpen: false, isLoading: true });

    StorageHelper.Cookie.remove('loginSession', { domain: '.squadintouch.com' });
    StorageHelper.Cookie.set('loginSession', loginSession);

    const userUpdated = {
      ...user,
      authorized: true,
      userId,
      loginSessionKey: key
    };

    getProfile(userUpdated)
      .then(profile => {
        onFormSubmit(userId, key, profile, { isRolesNotExist: false });
        history.push({ pathname: '/calendar' });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  };

  closeParentRoleRequestModal = () => {
    this.setState({
      isRoleParentRequestModalOpen: false
    });
  };

  closeStaffRoleRequestModal = () => {
    this.setState({
      isRoleStaffRequestModalOpen: false
    });
  };

  onClearSelectedSchool = () => {
    this.setState({
      school: undefined,
      schoolId: '',
      oneOffCode: '',
      isStaffRoleSelectorEnabled: false,
      isParentDetailsEnabled: false,
      isRoleStudentRequestModalOpen: false,
      isRoleStaffRequestModalOpen: false,
      isRoleParentRequestModalOpen: false,
      isAutoLinkSuccessModalOpen: false,
      parentDetailsDraft: undefined,
      registrationActionError: undefined
    });
  };

  createRequest(data, redirectPath: string = '/profile/requests') {
    const { loginSession } = this.state;
    const { history, user, onFormSubmit } = this.props;
    const { userId, key } = loginSession;

    createProfileRequest({ loginSessionKey: key }, data)
      .then(res => {
        StorageHelper.Cookie.remove('loginSession', { domain: '.squadintouch.com' });
        StorageHelper.Cookie.set('loginSession', loginSession);

        const userUpdated = {
          ...user,
          authorized: true,
          userId,
          loginSessionKey: key
        };

        return getProfile(userUpdated);
      })
      .then(profile => {
        onFormSubmit(userId, key, profile, { isRolesNotExist: true });

        history.push({
          pathname: redirectPath
        });
      });
  }

  onFirstStaffRoleRequestClick = (trialModules: string[]) => {
    const { schoolId, loginSession } = this.state;
    const { user, onFormSubmit } = this.props;
    const { userId, key } = loginSession;

    this.setState({
      isLoading: true,
      registrationActionError: undefined
    });

    const data = {
      schoolId,
      preset: ROLE.ADMIN,
      trialModules
    };

    createProfileRequest({ loginSessionKey: key }, data)
      .then(() => {
        StorageHelper.Cookie.remove('loginSession', { domain: '.squadintouch.com' });
        StorageHelper.Cookie.set('loginSession', loginSession);

        const userUpdated = {
          ...user,
          authorized: true,
          userId,
          loginSessionKey: key
        };

        return getProfile(userUpdated);
      })
      .then(profile => {
        this.setState({
          isLoading: false,
          registrationDone: 'adminTrial',
          registrationDoneProfile: profile,
          trialModules
        });
      })
      .catch(e => {
        this.setState({
          isLoading: false,
          isRoleFirstStaffRequestModalOpen: true,
          registrationActionError: this.extractErrText(
            e,
            'Unable to activate your school trial right now. Please try again later.'
          )
        });
      });
  };

  onStaffRoleRequestClick = () => {
    const { schoolId, staff } = this.state;

    this.setState({
      isLoading: true
    });

    const data = {
      schoolId: schoolId,
      preset: staff
    };

    this.createRequest(data);
  };

  onParentRoleRequestClick = () => {
    const { schoolId, students, loginSession } = this.state;
    const { history, user, onFormSubmit } = this.props;
    const { userId, key } = loginSession;

    this.setState({
      isLoading: true
    });

    const isStudentsExist = students.length > 0;

    if (isStudentsExist) {
      BPromise.all(
        students.map(student => {
          const data = {
            ...student,

            schoolId: schoolId,
            preset: ROLE.PARENT
          };
          return createProfileRequest({ loginSessionKey: key }, data);
        })
      )
        .then(res => {
          StorageHelper.Cookie.remove('loginSession', { domain: '.squadintouch.com' });
          StorageHelper.Cookie.set('loginSession', loginSession);

          const userUpdated = {
            ...user,
            authorized: true,
            userId: userId,
            loginSessionKey: key
          };

          return getProfile(userUpdated);
        })
        .then(profile => {
          onFormSubmit(userId, key, profile, { isRolesNotExist: true });

          history.push({
            pathname: '/profile/requests'
          });
        });
    } else {
      const data = {
        schoolId: schoolId,
        preset: ROLE.PARENT
      };

      this.createRequest(data);
    }
  };

  onStudentRoleRequestClick = () => {
    const { schoolId, permission, oneOffCode } = this.state;

    this.setState({
      isLoading: true
    });

    const data = oneOffCode
      ? {
          schoolId: schoolId,
          preset: permission,
          details: { oneOffCode: oneOffCode }
        }
      : {
          schoolId: schoolId,
          preset: permission
        };

    this.createRequest(data);
  };

  closeStudentRoleRequestModal = () => {
    this.setState({
      isRoleStudentRequestModalOpen: false
    });
  };

  extractErrText = (e: any, fallback: string) => {
    return (
      propz.get(e, ['response', 'data', 'details', 'text']) ||
      propz.get(e, ['response', 'data', 'message']) ||
      propz.get(e, ['response', 'data', 'error']) ||
      propz.get(e, ['message']) ||
      fallback
    );
  };

  getCooldownSecRemaining = () => {
    const until = this.state.registrationCooldownUntilMs;
    if (!until) return 0;
    const diffMs = until - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  private getRemainingSec = (untilMs?: number) => {
    if (!untilMs) return 0;
    const diffMs = untilMs - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  onEmailResendCooldownFinished = () => {
    this.setState({
      emailCooldownUntilMs: undefined,
      emailCooldownCode: undefined,
      emailResendError: undefined
    });
  };

  onEmailVerifyCooldownFinished = () => {
    this.setState({
      emailVerifyCooldownUntilMs: undefined,
      emailVerifyCooldownCode: undefined,
      emailVerifyError: undefined
    });
  };

  onPhoneResendCooldownFinished = () => {
    this.setState({
      phoneCooldownUntilMs: undefined,
      phoneCooldownCode: undefined,
      phoneResendError: undefined
    });
  };

  onPhoneVerifyCooldownFinished = () => {
    this.setState({
      phoneVerifyCooldownUntilMs: undefined,
      phoneVerifyCooldownCode: undefined,
      phoneVerifyError: undefined
    });
  };

  getEmailUpdateCooldownSecRemaining = () => {
    const until = this.state.emailUpdateCooldownUntilMs;
    if (!until) return 0;
    const diffMs = until - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  getPhoneUpdateCooldownSecRemaining = () => {
    const until = this.state.phoneUpdateCooldownUntilMs;
    if (!until) return 0;
    const diffMs = until - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  onEmailUpdateCooldownFinished = () => {
    this.setState({
      emailUpdateCooldownUntilMs: undefined,
      emailUpdateCooldownCode: undefined,
      emailActionError: undefined
    });
  };

  onPhoneUpdateCooldownFinished = () => {
    this.setState({
      phoneUpdateCooldownUntilMs: undefined,
      phoneUpdateCooldownCode: undefined,
      phoneActionError: undefined
    });
  };

  formatMmSs = (totalSec: number) => {
    const s = Math.max(0, Math.floor(totalSec));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    const ss2 = ss < 10 ? `0${ss}` : String(ss);
    return `${mm}:${ss2}`;
  };

  onSendEmailCodeAgainClick = event => {
    event.preventDefault();

    if (this.getEmailResendCooldownSecRemaining() > 0) return;

    const { loginSession } = this.state;
    const user = { loginSessionKey: loginSession.key };

    this.setState({ emailResendError: undefined });

    resendEmailCode(user)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          this.setState({
            emailResendError: res.message || 'We cannot resend an email code right now. Please try again later.',
            emailCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            emailCooldownCode: retryAfterSec > 0 ? code : undefined,
            isResendEmailCodeModalOpen: false
          });
          return;
        }

        const nextResendAtMs = res?.nextResendAt ? new Date(res.nextResendAt).getTime() : undefined;

        this.setState({
          isResendEmailCodeModalOpen: true,
          emailCooldownUntilMs: nextResendAtMs ? nextResendAtMs + 1000 : undefined,
          emailCooldownCode: undefined,
          emailResendError: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        this.setState({
          emailResendError: this.extractErrText(e, 'We cannot resend an email code right now. Please try again later.'),
          emailCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          emailCooldownCode: retryAfterSec > 0 ? code : undefined,
          isResendEmailCodeModalOpen: false
        });
      });
  };

  onSendPhoneCodeAgainClick = event => {
    event.preventDefault();

    if (this.getPhoneResendCooldownSecRemaining() > 0) return;

    const { loginSession } = this.state;
    const user = { loginSessionKey: loginSession.key };

    this.setState({ phoneResendError: undefined });

    resendPhoneCode(user)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;

          this.setState({
            phoneResendError: res.message || 'We cannot resend an SMS code right now. Please try again later.',
            phoneCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            phoneCooldownCode: retryAfterSec > 0 ? code : undefined,
            isResendPhoneCodeModalOpen: false
          });
          return;
        }

        const nextResendAtMs = res?.nextResendAt ? new Date(res.nextResendAt).getTime() : undefined;

        this.setState({
          isResendPhoneCodeModalOpen: true,
          phoneCooldownUntilMs: nextResendAtMs ? nextResendAtMs + 1000 : undefined,
          phoneCooldownCode: undefined,
          phoneResendError: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        this.setState({
          phoneResendError: this.extractErrText(
            e,
            'We cannot send an SMS code yet. Please verify your email address first.'
          ),
          phoneCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          phoneCooldownCode: retryAfterSec > 0 ? code : undefined,
          isResendPhoneCodeModalOpen: false
        });
      });
  };

  onChangePhoneClick = event => {
    event.preventDefault();

    const { isEmailConfirmed } = this.state;

    if (!isEmailConfirmed) {
      this.setState({
        phoneActionError:
          'Please verify your email first. You’ll be able to change your phone number after your email is confirmed.'
      });
      return;
    }

    this.setState({
      isChangePhoneModalOpen: true,
      phoneActionError: undefined
    });
  };

  onChangeEmailClick = event => {
    event.preventDefault();
    this.setState({
      isChangeEmailModalOpen: true
    });
  };

  onSubmitEmailClick = ({ emailCode }) => {
    const { loginSession } = this.state;
    const user = { loginSessionKey: loginSession.key };

    this.setState({ emailVerifyError: undefined });

    verifyEmail(user, emailCode)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;
          const text =
            propz.get(res, ['details', 'text']) || res.message || 'Too many attempts. Please try again later.';

          this.setState({
            isEmailConfirmed: false,
            emailVerifyError: text,
            emailVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            emailVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
          });
          return;
        }

        const confirmed = !!res?.confirmed;

        this.setState({
          isEmailConfirmed: confirmed,
          emailVerifyCooldownUntilMs: undefined,
          emailVerifyCooldownCode: undefined,
          emailVerifyError: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        this.setState({
          isEmailConfirmed: false,
          emailVerifyError: this.extractErrText(e, 'We cannot verify your email right now. Please try again later.'),
          emailVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          emailVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  onSubmitPhoneClick = ({ phoneCode }) => {
    const { loginSession } = this.state;
    const user = { loginSessionKey: loginSession.key };

    this.setState({ phoneVerifyError: undefined });

    verifyPhone(user, phoneCode)
      .then((res: any) => {
        if (res && res.isError) {
          const retryAfterSec = Number(res.retryAfterSec || 0) || 0;
          const code = String(res.code || '') || undefined;
          const text =
            propz.get(res, ['details', 'text']) || res.message || 'Too many attempts. Please try again later.';

          this.setState({
            isPhoneConfirmed: false,
            phoneVerifyError: text,
            phoneVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
            phoneVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
          });
          return;
        }

        const confirmed = !!res?.confirmed;

        this.setState({
          isPhoneConfirmed: confirmed,
          phoneVerifyCooldownUntilMs: undefined,
          phoneVerifyCooldownCode: undefined,
          phoneVerifyError: undefined
        });
      })
      .catch(e => {
        const payload = propz.get(e, ['response', 'data']);
        const retryAfterSec = Number(propz.get(payload, ['retryAfterSec'], 0)) || 0;
        const code = String(propz.get(payload, ['code'], '')) || undefined;

        this.setState({
          isPhoneConfirmed: false,
          phoneVerifyError: this.extractErrText(
            e,
            'We cannot verify your phone number right now. Please try again later.'
          ),
          phoneVerifyCooldownUntilMs: retryAfterSec > 0 ? Date.now() + (retryAfterSec + 1) * 1000 : undefined,
          phoneVerifyCooldownCode: retryAfterSec > 0 ? code : undefined
        });
      });
  };

  renderRegistrationDone() {
    const { history, onFormSubmit } = this.props;
    const { school, loginSession, registrationDoneProfile, trialModules } = this.state;
    const schoolName = propz.get(school, ['name'], 'your school');
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 30);
    const trialEnd = trialEndDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const startSchoolSetup = () => {
      if (registrationDoneProfile && loginSession) {
        onFormSubmit(loginSession.userId, loginSession.key, registrationDoneProfile, {
          isRolesNotExist: false
        });
      }
      history.push({ pathname: '/school/setup' });
    };

    return (
      <div className="eSchoolSignupLayout eSchoolSignupLayoutDone">
        {this.renderStepRail()}
        <main className="eSchoolSignupMain eRegistrationDoneMain">
          <div className="eRegistrationDone">
            <div className="eRegistrationDoneIcon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h1 className="eRegistrationDoneTitle">Your trial is ready</h1>
            <p className="eRegistrationDoneText">
              You are now a <strong>School Admin</strong> for {schoolName}. Your selected modules —{' '}
              <strong>
                {trialModules
                  .map(module =>
                    module === 'SPORT'
                      ? 'Sport & events'
                      : module === 'CLUBS'
                      ? 'Clubs & activities'
                      : module.charAt(0) + module.slice(1).toLowerCase()
                  )
                  .join(', ')}
              </strong>{' '}
              — run until <strong>{trialEnd}</strong>.
            </p>

            <div className="eRegistrationNextSteps">
              <div className="eRegistrationNextStep">
                <span className="eRegistrationNextStepIcon">
                  <FontAwesomeIcon icon={faUserFriends} />
                </span>
                <span>
                  <strong>Invite your colleagues</strong>
                  <small>Add staff to the same school workspace, with the right access for each role.</small>
                </span>
              </div>
              <div className="eRegistrationNextStep">
                <span className="eRegistrationNextStepIcon">
                  <FontAwesomeIcon icon={faSchool} />
                </span>
                <span>
                  <strong>Import pupils and set up your workspace</strong>
                  <small>
                    Upload your pupil spreadsheet, then add staff and houses and tailor the settings to your school.
                  </small>
                </span>
              </div>
              <div className="eRegistrationNextStep">
                <span className="eRegistrationNextStepIcon">
                  <FontAwesomeIcon icon={faGraduationCap} />
                </span>
                <span>
                  <strong>Plan a real school week</strong>
                  <small>Add a few clubs, events, fixtures or messages and see everything working together.</small>
                </span>
              </div>
            </div>

            <div className="eRegistrationDoneActions">
              <button type="button" className="btn btn-primary eRegistrationSubmitButton" onClick={startSchoolSetup}>
                <span>Set up my school</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  renderStepRail() {
    const currentStepNumber = this.getVisualStepNumber();
    const {
      personalDetailsDraft,
      email,
      phone,
      isEmailConfirmed,
      isPhoneConfirmed,
      permission,
      school,
      registrationDone
    } = this.state;
    const roleLabels = {
      STAFF: 'Member of staff',
      PARENT: 'Parent or guardian',
      STUDENT: 'Student'
    };
    const summary: Array<{ label: string; value: string }> = [];
    const schoolName = propz.get(school, ['name'], '—');
    const schoolPic = propz.get(school, ['pic'], '');
    const schoolAddress = String(propz.get(school, ['address'], '') || '').trim();
    const schoolPostcode = String(propz.get(school, ['postcode', 'postcode'], '') || '').trim();
    const normalisedAddress = schoolAddress.replace(/\s+/g, '').toUpperCase();
    const normalisedPostcode = schoolPostcode.replace(/\s+/g, '').toUpperCase();
    const schoolLocation =
      schoolAddress && schoolPostcode && normalisedAddress.indexOf(normalisedPostcode) === -1
        ? `${schoolAddress}, ${schoolPostcode}`
        : schoolAddress || schoolPostcode;
    const schoolInitials = String(schoolName)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 3)
      .map(part => part.charAt(0).toUpperCase())
      .join('');

    if (currentStepNumber > 1) {
      const firstName = propz.get(personalDetailsDraft, ['firstName'], '');
      const lastName = propz.get(personalDetailsDraft, ['lastName'], '');
      summary.push({ label: 'Name', value: `${firstName} ${lastName}`.trim() || '—' });
      summary.push({ label: 'Email', value: email || propz.get(personalDetailsDraft, ['email'], '—') });
      if (phone) summary.push({ label: 'Mobile', value: phone });
    }
    if (currentStepNumber > 2 && isEmailConfirmed && isPhoneConfirmed) {
      summary.push({ label: 'Verified', value: phone ? 'Email and mobile' : 'Email' });
    }
    if (permission) summary.push({ label: 'Role', value: roleLabels[permission] || permission });
    if (school) summary.push({ label: 'School', value: schoolName });
    if (registrationDone) summary.push({ label: 'Granted', value: 'School Admin · 30-day trial' });

    return (
      <aside className="eSchoolSignupRail">
        <div className="eSchoolSignupEyebrow">Progress</div>
        <div className="eSchoolSignupRailSteps">
          {VISUAL_REGISTRATION_STEPS.map((step, index) => {
            const stepNumber = index + 1;
            const isDone = stepNumber < currentStepNumber || currentStepNumber === 5;
            const isActive = stepNumber === currentStepNumber;
            return (
              <div
                key={step.id}
                className={`eSchoolSignupRailStep ${isDone ? 'isDone' : ''} ${isActive ? 'isActive' : ''}`}
              >
                <span className="eSchoolSignupRailDot">{isDone ? '✓' : stepNumber}</span>
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>

        {summary.length > 0 && (
          <div className="eSchoolSignupSummary">
            <div className="eSchoolSignupEyebrow">So far</div>
            {summary.map(item => (
              <div className="eSchoolSignupSummaryItem" key={item.label}>
                <span>{item.label}</span>
                {item.label === 'School' && school ? (
                  <div className="eSchoolSignupSummarySchool">
                    <span className="eSchoolSignupSummarySchoolLogo" aria-hidden="true">
                      <span>{schoolInitials || 'S'}</span>
                      {schoolPic && (
                        <img
                          src={schoolPic}
                          alt=""
                          onError={event => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    </span>
                    <span className="eSchoolSignupSummarySchoolCopy">
                      <strong>{item.value}</strong>
                      {schoolLocation && <small>{schoolLocation}</small>}
                    </span>
                  </div>
                ) : (
                  <strong>{item.value}</strong>
                )}
              </div>
            ))}
          </div>
        )}
      </aside>
    );
  }

  renderSignupHeader() {
    const currentStepNumber = this.getVisualStepNumber();
    const isStaffRegistration = this.getRequestedRole() === 'staff';

    return (
      <header className="eSchoolSignupHeader">
        <div className="eSchoolSignupHeaderInner">
          <img src="/dist/images/logoImage.png" alt="Squad In Touch" />
          <span className="eSchoolSignupHeaderTitle">
            {isStaffRegistration ? 'Create your school account' : 'Create your Squad In Touch account'}
          </span>
          <div className="eSchoolSignupHeaderSteps" aria-label="Registration progress">
            {VISUAL_REGISTRATION_STEPS.map((step, index) => {
              const stepNumber = index + 1;
              const isDone = stepNumber < currentStepNumber || currentStepNumber === 5;
              const isActive = stepNumber === currentStepNumber;
              return (
                <span
                  key={step.id}
                  className={`eSchoolSignupHeaderStep ${isDone ? 'isDone' : ''} ${isActive ? 'isActive' : ''}`}
                >
                  <span>{isDone ? '✓' : stepNumber}</span>
                  {step.label}
                </span>
              );
            })}
          </div>
          <Link className="eSchoolSignupSignIn" to="/login">
            Already have an account? Sign in
          </Link>
        </div>
      </header>
    );
  }

  render() {
    const {
      currentStep,
      isLoading,
      isResendEmailCodeModalOpen,
      isResendPhoneCodeModalOpen,
      isChangeEmailModalOpen,
      isChangePhoneModalOpen,
      isSuccessPhoneChangeModalOpen,
      isSuccessEmailChangeModalOpen,
      registrationDone
    } = this.state;
    const isModalOpen =
      isResendEmailCodeModalOpen ||
      isResendPhoneCodeModalOpen ||
      isChangeEmailModalOpen ||
      isChangePhoneModalOpen ||
      isSuccessPhoneChangeModalOpen ||
      isSuccessEmailChangeModalOpen;

    const classes = isModalOpen
      ? 'registration-container registration-container--ds modal-open'
      : 'registration-container registration-container--ds';

    return (
      <div className={classes}>
        {isLoading && (
          <div className="eRegistrationLoadingOverlay">
            <Loader />
          </div>
        )}
        {this.renderEmailChangeModal()}
        {this.renderPhoneChangeModal()}
        {this.renderResendEmailCodeModal()}
        {this.renderResendPhoneCodeModal()}
        {this.renderSuccessEmailChangeModal()}
        {this.renderSuccessPhoneChangeModal()}
        {this.renderSignupHeader()}
        {registrationDone === 'adminTrial' ? (
          this.renderRegistrationDone()
        ) : (
          <div className="eSchoolSignupLayout">
            {this.renderStepRail()}
            <main className="eSchoolSignupMain">{this.renderViewByStep()}</main>
          </div>
        )}
      </div>
    );
  }
}
